import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { restoreSession, startLookAlong } from './lib/auth';
import * as impersonation from './lib/impersonation';
import { initSentry, SentryErrorBoundary } from './lib/observability';
import './index.css';
import { i18nReady } from './i18n';

// THE FIRST THING THIS FILE DOES, and the order is the whole point.
//
// A look-along arrives as a URL fragment on a tab the operator just opened, and
// it has to be in place BEFORE anything asks which session this tab is in --
// restoreSession() below, and ProtectedRoute on the first render.
//
// It must also happen BEFORE initSentry(). Sentry's breadcrumb integration
// patches history.replaceState, and readFromHash() wipes the fragment with
// exactly that call -- so with Sentry already listening, the wipe records its
// own `from: "/app/#impersonate=<token>..."`, and a working credential for a
// customer's account sits in a third-party log for the life of the tab.
// Measured, not reasoned. The fragment was chosen over a query string to keep
// the token out of other people's logs; initialising Sentry first put it back.
const lookAlong = impersonation.readFromHash();
if (lookAlong) startLookAlong(lookAlong.token, lookAlong.tenantName, lookAlong.expiresAt);

initSentry();

// A tab opened before a deploy still asks for the old hashed chunks, and those
// are gone: "error loading dynamically imported module …/FacetApprovalPage-<hash>.js"
// (Sentry OUTFLANQ-INFRA-VQ, 2026-09-28). The hash is in the message, so every
// deploy made a new high-priority issue and a mail. Reload once for the new
// index.html; a second failure within a minute is a real error and surfaces.
window.addEventListener('vite:preloadError', (event) => {
  let recent = false;
  try {
    recent = Date.now() - Number(sessionStorage.getItem('chunkReloadAt') ?? 0) < 60_000;
    sessionStorage.setItem('chunkReloadAt', String(Date.now()));
  } catch {
    // Storage blocked: reload anyway, the guard is only a loop stop.
  }
  if (recent) return;
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Default is 3 retries with backoff: a failing endpoint kept the page
      // skeleton up for ~7 s before its error banner. One retry covers a
      // transient blip; anything else should show up fast.
      retry: 1,
      // Interval-refetch pauzeert in een achtergrond-tab; zonder focus-refetch
      // stond de pagina bij terugkomst stil tot je F5 drukte. staleTime dekt
      // het storm-risico af (max 1 refetch per 30s per query).
      refetchOnWindowFocus: true,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root missing from index.html');
}

// One refresh attempt before the first render. The access token lives in memory
// now, so after a reload the app knows nothing until the refresh cookie has been
// traded in — and ProtectedRoute reads that state synchronously. Render first
// and every F5 would flash the login screen at someone who is signed in.
//
// Awaiting one request before paint is the whole cost, and it is a request the
// app would make anyway. Doing it here rather than in a React loading state
// keeps ProtectedRoute a plain boolean check instead of a three-state machine.
// A failure is silent by design: no session is the normal state of a visitor
// arriving at the login page.
//
// `.finally` rather than top-level await: that would make this module's build
// output depend on the esbuild target, and a render that never happens because
// of a build flag is a bad trade for two saved lines.
const root = createRoot(rootElement);

// The active locale loads in parallel with the refresh (it is a lazy chunk now,
// not part of the main bundle); waiting on both keeps first paint translated.
// restoreSession() is a no-op during a look-along — it would trade the
// operator's refresh cookie for a full-rights token and quietly outrank the
// read-only one. The fence is in auth.ts, at the single function that trades
// the cookie; this call is left as it was so there is only one place to read.
void Promise.allSettled([restoreSession(), i18nReady]).then(() =>
  root.render(
  <StrictMode>
    <SentryErrorBoundary
      fallback={
        <main className="min-h-screen bg-bg-primary text-text-primary p-lg">
          <h1 className="font-sans text-h2 mb-md">Er ging iets mis.</h1>
          <p className="text-text-secondary">
            Probeer de pagina te verversen. Het probleem is automatisch gerapporteerd.
          </p>
        </main>
      }
    >
      <QueryClientProvider client={queryClient}>
        {/* basename = the Vite base ('/app/' in production, '/' in dev), so every
            route in App.tsx stays written as '/products' while the browser URL
            reads '/app/products'. Without it the router would match '/app/...'
            against its own table and render nothing but the 404 branch. */}
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </SentryErrorBoundary>
  </StrictMode>,
  ),
);
